import * as React from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'

interface UserSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  children: React.ReactNode
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

export function UserSheet({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  returnFocusRef,
}: UserSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        onCloseAutoFocus={(event) => {
          if (!returnFocusRef?.current) return
          event.preventDefault()
          returnFocusRef.current.focus()
        }}
        className="flex h-full !w-full max-w-[calc(100vw-1rem)] flex-col gap-0 p-0 sm:!w-[460px] sm:!max-w-[460px]"
      >
        <SheetHeader className="space-y-1.5 border-b border-primary/5 px-5 pb-5 pt-6 text-left sm:px-7 sm:pt-7">
          <SheetTitle className="pr-8 font-serif text-[26px] font-medium leading-tight tracking-tight text-primary">
            {title}
          </SheetTitle>
          {subtitle && (
            <SheetDescription className="pr-8 text-[13px] leading-relaxed text-primary/70">
              {subtitle}
            </SheetDescription>
          )}
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  )
}

export function UserSheetBody({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex-1 space-y-5 overflow-y-auto px-5 pb-8 pt-6 sm:px-7',
        className
      )}
    >
      {children}
    </div>
  )
}

export function UserSheetFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end gap-2 border-t border-primary/5 bg-background px-5 py-4 sm:px-7">
      {children}
    </div>
  )
}

export function UserSheetFieldLabel({
  children,
  className,
  htmlFor,
}: {
  children: React.ReactNode
  className?: string
  htmlFor?: string
}) {
  const labelClassName = cn(
    'mb-2 block text-[11px] font-medium uppercase tracking-[0.14em] text-primary/70',
    className
  )

  if (htmlFor) {
    return (
      <label htmlFor={htmlFor} className={labelClassName}>
        {children}
      </label>
    )
  }

  return (
    <Typography as="span" variant="body-sm" className={labelClassName}>
      {children}
    </Typography>
  )
}
